/* eslint-disable no-magic-numbers */

import {
  Button,
  DateFormats,
  DescriptionList,
  formatISODate,
  formatNumber,
  Inline,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useContractor } from '@omnipresentgroup/domains';
import { type Contract, ContractState } from '@omnipresentgroup/types';
import { useMemo, useState } from 'react';

import { ContractorContractingDetailsEditModal } from './ContractorContractingDetailsEditModal';

type Renderer = (contract: Contract) => string | JSX.Element;

const CONTRACTING_ENTRIES = [
  {
    key: 'amount',
    label: 'Amount',
  },

  {
    key: 'startDate',
    label: 'Start date',
  },
  {
    key: 'endDate',
    label: 'End date',
  },
];

const dateRenderer = (date?: string) =>
  date ? formatISODate(date, DateFormats.Date, true, '-') : '-';

const amountRenderer = (contract: Contract) => {
  if (contract.rate) {
    return `${formatNumber({
      input: Number(contract.rate),
      currency: contract.invoicingCurrency,
    })} ${
      contract.expectedInvoicingFrequency
        ? contract.expectedInvoicingFrequency.replaceAll('_', ' ').toLowerCase()
        : ''
    }`;
  }
  return '-';
};

const contractingRendererMapping: {
  [key: (typeof CONTRACTING_ENTRIES)[number]['key']]: Renderer;
} = {
  amount: (contract: Contract) => amountRenderer(contract),
  startDate: (contract: Contract) => dateRenderer(contract.startDate),
  endDate: (contract: Contract) => dateRenderer(contract.endDate),
};

export const ContractorContractingDetails = ({
  contractorId,
  showEdit = false,
}: {
  contractorId: string;
  showEdit?: boolean;
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);

  const { data: contractor, isLoading } = useContractor({ id: contractorId });

  const contract = useMemo(
    () => contractor?.contracts[0],
    [contractor?.contracts],
  );

  const canEdit = useMemo(
    () => contract?.state === ContractState.ACTIVE,
    [contract?.state],
  );

  const contractingDetails = useMemo(
    () =>
      CONTRACTING_ENTRIES.map(({ label, key }) => ({
        header: label,
        detail: isLoading ? (
          <Skeleton key={key} />
        ) : // eslint-disable-next-line unicorn/no-nested-ternary
        contract &&
          typeof contractingRendererMapping[
            key as (typeof CONTRACTING_ENTRIES)[number]['key']
          ] === 'function' ? (
          contractingRendererMapping?.[key](contract)
        ) : (
          (contract?.[key as keyof Contract] as string | JSX.Element)
        ),
      })),
    [contract, isLoading],
  );

  return (
    <Stack data-testid="contracting-details-list">
      <Inline justify="space-between" align="center">
        <Typography as="h3" hideParagraphSpacing size="20" weight="medium">
          Contracting details
        </Typography>
        {showEdit && (
          <Button
            icon="Edit"
            size="small"
            variant="secondary"
            onClick={() => {
              setEditModalOpen(true);
            }}
            disabled={!canEdit}
          >
            Edit
          </Button>
        )}
      </Inline>
      <DescriptionList
        fraction="1/2"
        id="contracting-details-list"
        list={contractingDetails}
      />
      {contract && editModalOpen && (
        <ContractorContractingDetailsEditModal
          modalProps={{
            open: editModalOpen,
            onOpenChange: setEditModalOpen,
          }}
          handleCancel={() => {
            setEditModalOpen(false);
          }}
          handleConfirm={() => {
            setEditModalOpen(false);
          }}
          contract={contract}
        />
      )}
    </Stack>
  );
};
